import React, { useState } from 'react';
import { Ruler } from 'lucide-react';
import { SEO } from '../components/SEO';

interface HeightState {
  fatherHeight: string;
  motherHeight: string;
  childGender: 'M' | 'F';
  currentHeight: string;
  currentAge: string;
}

function KalkulatorVisineDjeteta() {
  const [values, setValues] = useState<HeightState>({
    fatherHeight: '',
    motherHeight: '',
    childGender: 'M',
    currentHeight: '',
    currentAge: ''
  });

  const [result, setResult] = useState<{
    predictedHeight: number;
    heightRange: { min: number; max: number };
    currentPercentile: number;
  } | null>(null);

  const calculateHeight = () => {
    const fatherHeight = parseFloat(values.fatherHeight);
    const motherHeight = parseFloat(values.motherHeight);
    const currentHeight = parseFloat(values.currentHeight);
    const currentAge = parseFloat(values.currentAge);
    
    if (isNaN(fatherHeight) || isNaN(motherHeight)) {
      return null;
    }

    // Khamis-Roche method for height prediction
    let predictedHeight: number;
    let heightRange: { min: number; max: number };

    if (values.childGender === 'M') {
      // Boys: (Father's height + (Mother's height + 13)) / 2
      predictedHeight = (fatherHeight + (motherHeight + 13)) / 2;
      heightRange = {
        min: predictedHeight - 8.5,
        max: predictedHeight + 8.5
      };
    } else {
      // Girls: ((Father's height - 13) + Mother's height) / 2
      predictedHeight = ((fatherHeight - 13) + motherHeight) / 2;
      heightRange = {
        min: predictedHeight - 8.5,
        max: predictedHeight + 8.5
      };
    }

    // Calculate current percentile if current height and age are provided
    let currentPercentile = 50;
    if (!isNaN(currentHeight) && !isNaN(currentAge)) {
      // Simplified percentile calculation
      const expectedHeightForAge = values.childGender === 'M' 
        ? (currentAge * 6.5 + 74) // Rough estimate for boys
        : (currentAge * 6.2 + 72); // Rough estimate for girls
      
      currentPercentile = ((currentHeight - expectedHeightForAge) / 10) * 50 + 50;
      currentPercentile = Math.max(0, Math.min(100, currentPercentile));
    }

    return {
      predictedHeight: parseFloat(predictedHeight.toFixed(1)),
      heightRange: {
        min: parseFloat(heightRange.min.toFixed(1)),
        max: parseFloat(heightRange.max.toFixed(1))
      },
      currentPercentile: parseFloat(currentPercentile.toFixed(1))
    };
  };

  const handleInputChange = (field: keyof HeightState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));

    const results = calculateHeight();
    setResult(results);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Visine Djeteta",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte predviđenu visinu djeteta u odrasloj dobi. Precizna procjena bazirana na visini roditelja i trenutnoj visini djeteta.",
    "url": "https://online-kalkulator.com/kalkulator-visine-djeteta",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Predviđanje konačne visine",
      "Izračun percentila rasta",
      "Praćenje razvoja",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Visine Djeteta | Predviđanje Konačne Visine"
        description="Izračunajte predviđenu visinu djeteta u odrasloj dobi. Precizna procjena bazirana na visini roditelja i trenutnoj visini djeteta."
        canonicalUrl="/kalkulator-visine-djeteta"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Ruler className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Visine Djeteta</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina oca (cm)
                  </label>
                  <input
                    type="number"
                    value={values.fatherHeight}
                    onChange={(e) => handleInputChange('fatherHeight', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 180"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Visina majke (cm)
                  </label>
                  <input
                    type="number"
                    value={values.motherHeight}
                    onChange={(e) => handleInputChange('motherHeight', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 165"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spol djeteta
                  </label>
                  <select
                    value={values.childGender}
                    onChange={(e) => handleInputChange('childGender', e.target.value as 'M' | 'F')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="M">Muško</option>
                    <option value="F">Žensko</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trenutna dob (godine)
                  </label>
                  <input
                    type="number"
                    value={values.currentAge}
                    onChange={(e) => handleInputChange('currentAge', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trenutna visina djeteta (cm)
                </label>
                <input
                  type="number"
                  value={values.currentHeight}
                  onChange={(e) => handleInputChange('currentHeight', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="npr. 140"
                />
              </div>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-gray-50">
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {result.predictedHeight} cm
                    </p>
                    <p className="text-gray-600">
                      Predviđena visina u odrasloj dobi
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Raspon visine:</span>
                    <span className="font-semibold">
                      {result.heightRange.min} - {result.heightRange.max} cm
                    </span>
                  </div>

                  {!isNaN(result.currentPercentile) && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Trenutni percentil:</span>
                      <span className="font-semibold">
                        {result.currentPercentile}%
                      </span>
                    </div>
                  )}

                  {/* Height Range Visualization */}
                  <div className="mt-4">
                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                        style={{ 
                          width: `${result.currentPercentile}%` 
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-500 mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Visine</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator visine djeteta koristi znanstveno utemeljene metode za procjenu konačne
                  visine djeteta u odrasloj dobi. Izračun se temelji na visini roditelja, spolu
                  djeteta i trenutnoj visini i dobi djeteta ako su dostupni.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Se Računa Visina</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Za dječake: (Visina oca + (Visina majke + 13)) / 2</li>
                  <li>Za djevojčice: ((Visina oca - 13) + Visina majke) / 2</li>
                  <li>Dodaje se raspon od ±8.5 cm za prirodnu varijaciju</li>
                  <li>Uzima se u obzir trenutna visina i dob za precizniju procjenu</li>
                  <li>Percentili pokazuju gdje se dijete nalazi u odnosu na vršnjake</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori Koji Utječu na Rast</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Genetika (visina roditelja)</li>
                  <li>Prehrana i kvaliteta ishrane</li>
                  <li>Fizička aktivnost</li>
                  <li>Kvaliteta sna</li>
                  <li>Opće zdravstveno stanje</li>
                  <li>Hormonalna ravnoteža</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je procjena i može odstupati od stvarne konačne visine</li>
                  <li>Ne uzima u obzir sve moguće faktore koji utječu na rast</li>
                  <li>Konzultirajte pedijatra za detaljniju procjenu</li>
                  <li>Redovito praćenje rasta je važnije od pojedinačnog mjerenja</li>
                  <li>Svako dijete raste svojim tempom</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kada Posjetiti Liječnika</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Ako je dijete značajno niže ili više od vršnjaka</li>
                  <li>Ako postoji nagli zastoj u rastu</li>
                  <li>Ako je rast neuobičajeno brz</li>
                  <li>Ako postoje drugi zdravstveni problemi</li>
                  <li>Za redovite kontrole rasta i razvoja</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorVisineDjeteta;